import * as React from 'react'
import dynamic from 'next/dynamic'

import { i18n } from '@thg-commerce/enterprise-core'
import { TrackStoreStockEvent } from '@thg-commerce/enterprise-metrics/src/data_layer/pushToDataLayer/utils'
import { AvailabilitySource } from '@thg-commerce/enterprise-network/src/generated/graphql'
import { useTheme } from '@thg-commerce/enterprise-pages/src/Product/theme'

import { useProductPage } from '../../context'

import { ArrowRight, StoreStock, StoreStockButton } from './styles'

const StoreStockModalPresenter = dynamic(
  () =>
    import('@thg-commerce/enterprise-components').then(
      (mod) => mod.StoreStockModalPresenter,
    ),
  { ssr: false },
)

export const ProductStoreStock = () => {
  const {
    selectedVariant,
    selectedOptions,
    setStoreStockActive,
    product,
  } = useProductPage()
  const theme = useTheme()
  const [open, setOpen] = React.useState(false)
  const previousSizeRef = React.useRef<string | undefined>()
  const hasMounted = React.useRef(false)

  React.useEffect(() => {
    if (!selectedVariant?.enableAddToBasket) {
      setStoreStockActive?.(false)
    }
  }, [selectedVariant, setStoreStockActive])

  React.useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true
      return
    }

    if (
      selectedOptions?.Size &&
      previousSizeRef.current !== selectedOptions.Size
    ) {
      TrackStoreStockEvent({
        event: 'ecom_event',
        event_name: 'size_prompt',
        action: 'size selected',
      })
    }

    previousSizeRef.current = selectedOptions?.Size
  }, [selectedOptions?.Size])

  const handleOpenModal = React.useCallback(() => {
    if (selectedVariant?.enableAddToBasket) {
      setOpen(true)
    } else {
      setStoreStockActive?.(true)
      TrackStoreStockEvent({
        event: 'ecom_event',
        event_name: 'size_prompt',
        action: 'display',
      })
    }
  }, [selectedVariant?.enableAddToBasket, setStoreStockActive])

  const i18nText = {
    storeStockButtonText: i18n('product.store.stock.button.text'),
  }

  if (selectedVariant?.availabilitySource === AvailabilitySource.Dropship) {
    return <React.Fragment />
  }

  const disabled = theme?.pageTheme?.productOptions?.showOutOfStockAsDisabled
    ? !selectedVariant?.inStock && selectedVariant?.enableAddToBasket !== false
    : false

  return (
    <React.Fragment>
      <StoreStockButton
        emphasis="medium"
        onClick={() => {
          TrackStoreStockEvent({
            event: 'ecom_event',
            event_name: 'check_instore_click',
          })
          handleOpenModal()
        }}
        disabled={disabled}
        data-testid="store-stock-button"
        hasBorderBottom={product?.allVariantsOutOfStock}
      >
        <StoreStock />
        {i18nText.storeStockButtonText} <ArrowRight />
      </StoreStockButton>
      <StoreStockModalPresenter
        open={open}
        setOpen={setOpen}
        product={{
          title: selectedVariant?.title || '',
          url: selectedVariant?.url || '',
          sku: selectedVariant?.sku,
          images: {
            largeProduct: selectedVariant?.images?.[0]?.largeProduct,
          },
          inStock: selectedVariant?.inStock,
          price: {
            displayValue: selectedVariant?.price?.price?.displayValue || '',
          },
          rrp: {
            displayValue: selectedVariant?.price?.rrp?.displayValue || '',
          },
          selectedOption: selectedOptions || {},
        }}
      />
    </React.Fragment>
  )
}
